import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSignOutAlt, FaPlus, FaEdit, FaTimes, FaChevronDown, FaChevronRight, FaSearch, FaDownload,
  FaChartPie, FaHandHoldingUsd, FaReceipt, FaUserFriends, FaStore, FaChartBar, FaBook,
  FaUserCircle, FaWallet, FaArrowDown, FaArrowUp, FaCoins, FaUsers, FaPlusCircle, FaMinusCircle,
  FaBuilding, FaMoneyBillWave, FaListAlt,
} from 'react-icons/fa';
import financeApi, { clearFinanceAuth, getFinanceUser } from './api';
import FlowSubTabs from './FlowSubTabs';
import { TabStory, FlowGuide, FormPanel, HelpTip, ListPanel } from './FinanceVisualGuide';
import PasswordInput from './PasswordInput';
import logo from '../assets/logo_v3.png';
import financeOverview from './assets/finance-overview.svg';

const TABS_ADMIN = ['overview', 'collections', 'expenses', 'employees', 'franchises', 'reports'];
const TABS_EMPLOYEE = ['overview', 'expenses', 'ledger'];

function formatINR(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);
}

function splitInclusiveGst(total, gstRate = 18) {
  const num = Number(total);
  if (!num || num <= 0) return null;
  const rate = Number(gstRate) / 100;
  const base = Math.round((num / (1 + rate)) * 100) / 100;
  const gst = Math.round((num - base) * 100) / 100;
  return { base, gst, total: num };
}

function currentMonthYearValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function buildExpenseMonthOptions() {
  const options = [];
  const now = new Date();
  let year = 2025;
  let month = 7;
  const endYear = now.getFullYear();
  const endMonth = now.getMonth() + 1;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = new Date(year, month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    options.push({ value, label });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return options.reverse();
}

const EXPENSE_MONTH_OPTIONS = buildExpenseMonthOptions();

const TAB_ICONS = {
  overview: FaChartPie,
  collections: FaHandHoldingUsd,
  expenses: FaReceipt,
  employees: FaUserFriends,
  franchises: FaStore,
  reports: FaChartBar,
  ledger: FaBook,
};

const COMPANY_FLOW_STEPS = [
  { icon: FaStore, label: 'Franchise store', desc: 'Pays GST-inclusive amount to company' },
  { icon: FaBuilding, label: 'Company account', desc: 'Money lands in the bank ledger' },
  { icon: FaReceipt, label: 'Pay & spend', desc: 'Used for vendors and team' },
];

const CASH_FLOW_STEPS = [
  { icon: FaStore, label: 'Franchise store', desc: 'Hands physical cash to team' },
  { icon: FaUsers, label: 'Employee', desc: 'Holds cash until it is spent' },
  { icon: FaReceipt, label: 'Log expense', desc: 'Every spend is recorded' },
];

function getTabStory(tab, { isAdmin, adminFlow }) {
  if (tab === 'overview') {
    if (!isAdmin) {
      return {
        icon: FaChartPie,
        title: 'Your money at a glance',
        subtitle: 'See how much cash you hold and what you have spent — like a simple picture of your wallet.',
        art: financeOverview,
      };
    }
    return adminFlow === 'company'
      ? {
          icon: FaBuilding,
          title: 'Company account snapshot',
          subtitle: 'Franchise payments come in, company expenses go out. These four cards tell that story in plain numbers.',
          art: financeOverview,
        }
      : {
          icon: FaMoneyBillWave,
          title: 'Cash flow snapshot',
          subtitle: 'Cash collected from stores, held by employees, and spent on the ground — all in one view.',
          art: financeOverview,
        };
  }
  if (tab === 'collections') {
    return adminFlow === 'company'
      ? {
          icon: FaHandHoldingUsd,
          title: 'Record a franchise payment',
          subtitle: 'When a store sends money to the company bank account, fill in the form below. GST is calculated for you.',
        }
      : {
          icon: FaMoneyBillWave,
          title: 'Record cash from a store',
          subtitle: 'When a franchise gives cash to an employee, pick the store and employee. Their balance updates automatically.',
        };
  }
  if (tab === 'expenses') {
    if (!isAdmin) {
      return {
        icon: FaReceipt,
        title: 'Log what you spent',
        subtitle: 'Every expense comes from your cash balance. Enter the amount, category, and date — that is it.',
      };
    }
    return adminFlow === 'company'
      ? {
          icon: FaReceipt,
          title: 'Pay from company account',
          subtitle: 'Pay a vendor directly, or send money to an employee (it adds to their cash balance). Choose one — not both.',
        }
      : {
          icon: FaReceipt,
          title: 'Log employee cash spend',
          subtitle: 'Record when an employee uses their held cash. Pick who spent it and what it was for.',
        };
  }
  if (tab === 'employees') {
    return {
      icon: FaUserFriends,
      title: 'Your field team',
      subtitle: 'Add employees who collect cash from franchises. Their balance grows when you record cash collections.',
    };
  }
  if (tab === 'franchises') {
    return {
      icon: FaStore,
      title: 'Franchise partners',
      subtitle: 'List every store in the network. Reference amounts are for planning only — they do not change the ledger.',
    };
  }
  if (tab === 'reports') {
    return adminFlow === 'company'
      ? {
          icon: FaChartBar,
          title: 'Company reports & export',
          subtitle: 'See franchise-wise totals, monthly trends, and download a full account book as Excel.',
        }
      : {
          icon: FaChartBar,
          title: 'Cash reports',
          subtitle: 'Track cash collected per franchise, employee outstanding balances, and monthly cash movement.',
        };
  }
  if (tab === 'ledger') {
    return {
      icon: FaBook,
      title: 'Your personal ledger',
      subtitle: 'A line-by-line story of every credit (cash in) and debit (spend) on your account this month.',
    };
  }
  return null;
}

function StatChart({ variant }) {
  const stroke = '#1e40af';
  const fill = 'rgba(30, 64, 175, 0.12)';

  if (variant === 'area') {
    return (
      <svg viewBox="0 0 140 44" className="finance-stat-chart-svg" aria-hidden="true">
        <path d="M0 36 L0 30 C18 28 28 18 42 22 C56 26 68 8 84 12 C100 16 118 6 140 10 L140 44 Z" fill={fill} />
        <path d="M0 30 C18 28 28 18 42 22 C56 26 68 8 84 12 C100 16 118 6 140 10" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (variant === 'bars') {
    return (
      <svg viewBox="0 0 140 44" className="finance-stat-chart-svg" aria-hidden="true">
        {[12, 22, 16, 30, 20, 34, 26].map((h, i) => (
          <rect key={i} x={8 + i * 18} y={44 - h} width="10" height={h} rx="3" fill={i === 5 ? stroke : fill} />
        ))}
      </svg>
    );
  }
  if (variant === 'line') {
    return (
      <svg viewBox="0 0 140 44" className="finance-stat-chart-svg" aria-hidden="true">
        <path d="M0 32 L24 28 L48 30 L72 18 L96 22 L120 10 L140 6" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="140" cy="6" r="3.5" fill={stroke} />
      </svg>
    );
  }
  if (variant === 'steps') {
    return (
      <svg viewBox="0 0 140 44" className="finance-stat-chart-svg" aria-hidden="true">
        <path d="M0 34 L0 26 L28 26 L28 20 L56 20 L56 14 L84 14 L84 8 L112 8 L112 4 L140 4" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (variant === 'ring') {
    return (
      <svg viewBox="0 0 140 44" className="finance-stat-chart-svg finance-stat-chart-svg--ring" aria-hidden="true">
        <circle cx="118" cy="22" r="16" fill="none" stroke={fill} strokeWidth="6" />
        <circle cx="118" cy="22" r="16" fill="none" stroke={stroke} strokeWidth="6" strokeDasharray="72 100" strokeLinecap="round" transform="rotate(-90 118 22)" />
        {[0, 1, 2, 3, 4].map((i) => (
          <rect key={i} x={8 + i * 16} y={44 - (10 + i * 4)} width="8" height={10 + i * 4} rx="2" fill={fill} />
        ))}
      </svg>
    );
  }
  return null;
}

function StatCard({ label, value, tone = 'primary', icon: Icon, info, chart = 'line', tag }) {
  return (
    <div className={`finance-stat-card finance-stat-card--${tone}`}>
      <div className="finance-stat-top">
        <div className="finance-stat-icon">
          <Icon />
        </div>
        <div className="finance-stat-meta">
          <div className="finance-stat-label-row">
            <span className="finance-stat-label">{label}</span>
            {tag && <span className="finance-stat-tag">{tag}</span>}
          </div>
          <div className="finance-stat-value">{value}</div>
        </div>
      </div>
      {info && <p className="finance-stat-info">{info}</p>}
      <div className="finance-stat-chart">
        <StatChart variant={chart} />
      </div>
    </div>
  );
}

function pct(part, total) {
  if (!total || total <= 0) return null;
  return Math.round((part / total) * 100);
}


function filterCollectionSearch(list, search) {
  if (!search.trim()) return list;
  const q = search.trim().toLowerCase();
  return list.filter((c) => {
    const text = [c.franchise_name, c.notes, c.assigned_employee_name].filter(Boolean).join(' ').toLowerCase();
    return text.includes(q);
  });
}

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const user = getFinanceUser();
  const userId = user?.id;
  const userRole = user?.role;
  const isAdmin = userRole === 'ADMIN';
  const tabs = isAdmin ? TABS_ADMIN : TABS_EMPLOYEE;

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [collections, setCollections] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [franchiseReport, setFranchiseReport] = useState([]);
  const [employeeCashReport, setEmployeeCashReport] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState([]);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [bookPeriod, setBookPeriod] = useState('monthly');
  const [bookMonth, setBookMonth] = useState(currentMonthYearValue);
  const [bookStartMonth, setBookStartMonth] = useState('2025-07');
  const [bookEndMonth, setBookEndMonth] = useState(currentMonthYearValue);
  const [bookDownloadYear, setBookDownloadYear] = useState(new Date().getFullYear());
  const [bookDownloading, setBookDownloading] = useState(false);

  const [adminFlow, setAdminFlow] = useState('company');
  const [companyPaymentForm, setCompanyPaymentForm] = useState({
    franchise_id: '', total_amount: '', gst_rate: '18',
    collection_date: new Date().toISOString().slice(0, 10), notes: '',
  });
  const [cashCollectionForm, setCashCollectionForm] = useState({
    franchise_id: '', cash_amount: '', assigned_employee_id: '',
    collection_date: new Date().toISOString().slice(0, 10), notes: '',
  });
  const [expenseForm, setExpenseForm] = useState({
    amount: '', source: 'EMPLOYEE_CASH', category: '', description: '',
    expense_date: new Date().toISOString().slice(0, 10), franchise_id: '', employee_id: '', vendor: '',
  });
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', password: '' });
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [employeePasswordForm, setEmployeePasswordForm] = useState('');
  const [franchiseForm, setFranchiseForm] = useState({
    name: '', city: '', total_amount_with_gst: '', cash_to_be_paid: '',
  });
  const [editingFranchiseId, setEditingFranchiseId] = useState(null);
  const [collectionFranchiseFilter, setCollectionFranchiseFilter] = useState('');
  const [collectionSearch, setCollectionSearch] = useState('');
  const [expandedCollectionGroups, setExpandedCollectionGroups] = useState({});
  const [expenseMonthFilter, setExpenseMonthFilter] = useState(currentMonthYearValue);
  const [ledgerMonthFilter, setLedgerMonthFilter] = useState(currentMonthYearValue);
  const [ledgerReport, setLedgerReport] = useState(null);

  const showMsg = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const apiGet = useCallback(async (url, params = {}) => {
    try {
      const res = await financeApi.get(url, { params });
      if (res.data?.status?.statusCode === 'code-200') return res.data.data;
    } catch (err) {
      console.error(`Finance API error [${url}]:`, err.response?.data || err.message);
    }
    return null;
  }, []);

  const fetchDashboard = useCallback(async () => {
    const data = await apiGet('/finance/dashboard');
    if (data) setDashboard(data);
  }, [apiGet]);

  const fetchCollections = useCallback(async () => {
    const params = {
      collection_type: adminFlow === 'company' ? 'COMPANY_PAYMENT' : 'CASH_TO_EMPLOYEE',
    };
    if (collectionFranchiseFilter) params.franchise_id = Number(collectionFranchiseFilter);
    const data = await apiGet('/finance/collections', params);
    if (data) setCollections(data);
  }, [adminFlow, collectionFranchiseFilter, apiGet]);

  const fetchExpenses = useCallback(async () => {
    const params = { month_year: expenseMonthFilter };
    if (userRole === 'ADMIN') {
      params.source = adminFlow === 'company' ? 'COMPANY_ACCOUNT' : 'EMPLOYEE_CASH';
    }
    try {
      const res = await financeApi.get('/finance/expenses', { params });
      if (res.data?.status?.statusCode === 'code-200') {
        setExpenses(res.data.data || []);
        return;
      }
      showMsg(res.data?.status?.statusMessage || 'Could not load expenses', true);
    } catch (err) {
      console.error('Finance API error [/finance/expenses]:', err.response?.data || err.message);
      showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Could not load expenses', true);
    }
  }, [expenseMonthFilter, adminFlow, userRole]);

  const fetchEmployees = useCallback(async () => {
    const data = await apiGet('/finance/employees');
    if (data) setEmployees(data);
  }, [apiGet]);

  const fetchFranchises = useCallback(async () => {
    const data = await apiGet('/finance/franchises');
    if (data) setFranchises(data);
  }, [apiGet]);

  const fetchReports = useCallback(async () => {
    const [fr, ec, mo] = await Promise.all([
      apiGet('/finance/reports/franchise-wise'),
      apiGet('/finance/reports/employee-cash'),
      apiGet('/finance/reports/monthly', { year: reportYear }),
    ]);
    if (fr) setFranchiseReport(fr);
    if (ec) setEmployeeCashReport(ec);
    if (mo) setMonthlyReport(mo);
  }, [reportYear, apiGet]);

  const fetchLedger = useCallback(async () => {
    const data = await apiGet('/finance/ledger/me', { month_year: ledgerMonthFilter });
    if (data) setLedgerReport(data);
  }, [ledgerMonthFilter, apiGet]);

  useEffect(() => {
    if (!userId) {
      navigate('/finance/login');
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchFranchises(),
        ...(userRole === 'ADMIN' ? [fetchEmployees()] : []),
      ]);
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [userId, userRole, navigate, fetchDashboard, fetchFranchises, fetchEmployees]);

  useEffect(() => {
    if (activeTab === 'reports' && userRole === 'ADMIN') {
      fetchReports();
    }
    if ((activeTab === 'employees' || activeTab === 'expenses') && userRole === 'ADMIN') {
      fetchEmployees();
    }
    if (activeTab === 'franchises' && userRole === 'ADMIN') {
      fetchFranchises();
    }
    if (activeTab === 'ledger' && userRole === 'EMPLOYEE') {
      fetchLedger();
    }
    if (activeTab === 'collections' && userRole === 'ADMIN') {
      fetchCollections();
    }
    if (activeTab === 'expenses') {
      fetchExpenses();
    }
  }, [
    activeTab,
    userRole,
    adminFlow,
    collectionFranchiseFilter,
    expenseMonthFilter,
    ledgerMonthFilter,
    fetchReports,
    fetchEmployees,
    fetchFranchises,
    fetchLedger,
    fetchCollections,
    fetchExpenses,
  ]);

  const handleTabChange = (tab) => {
    if (isAdmin) setAdminFlow('company');
    setActiveTab(tab);
  };

  const handleAdminFlowChange = (flow) => {
    setAdminFlow(flow);
  };

  useEffect(() => {
    setCollectionFranchiseFilter('');
    setCollectionSearch('');
    setExpandedCollectionGroups({});
    setExpenseForm((prev) => ({ ...prev, employee_id: '', vendor: '' }));
  }, [adminFlow]);

  const handleLogout = () => {
    clearFinanceAuth();
    navigate('/finance/login');
  };

  const submitCollection = async (collectionType, payload, successMessage, resetForm) => {
    setLoading(true);
    try {
      const res = await financeApi.post('/finance/collections', {
        collection_type: collectionType,
        ...payload,
      });
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg(successMessage);
        fetchCollections();
        fetchDashboard();
        if (collectionType === 'CASH_TO_EMPLOYEE') fetchEmployees();
        resetForm();
      } else {
        showMsg(res.data?.status?.statusMessage || 'Failed', true);
      }
    } catch (err) {
      showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Error', true);
    } finally {
      setLoading(false);
    }
  };

  const companyPaymentBreakdown = splitInclusiveGst(companyPaymentForm.total_amount, companyPaymentForm.gst_rate);

  const handleCompanyPayment = async (e) => {
    e.preventDefault();
    await submitCollection(
      'COMPANY_PAYMENT',
      {
        franchise_id: Number(companyPaymentForm.franchise_id),
        total_amount: Number(companyPaymentForm.total_amount),
        gst_rate: Number(companyPaymentForm.gst_rate || 18),
        cash_amount: 0,
        collection_date: companyPaymentForm.collection_date,
        notes: companyPaymentForm.notes,
      },
      'Company payment recorded',
      () => setCompanyPaymentForm({ ...companyPaymentForm, total_amount: '', notes: '' })
    );
  };

  const handleCashCollection = async (e) => {
    e.preventDefault();
    await submitCollection(
      'CASH_TO_EMPLOYEE',
      {
        franchise_id: Number(cashCollectionForm.franchise_id),
        franchise_amount: 0,
        gst_amount: 0,
        cash_amount: Number(cashCollectionForm.cash_amount),
        assigned_employee_id: Number(cashCollectionForm.assigned_employee_id),
        collection_date: cashCollectionForm.collection_date,
        notes: cashCollectionForm.notes,
      },
      'Cash collection recorded',
      () => setCashCollectionForm({ ...cashCollectionForm, cash_amount: '', assigned_employee_id: '', notes: '' })
    );
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...expenseForm,
        source: 'EMPLOYEE_CASH',
        amount: Number(expenseForm.amount),
        franchise_id: expenseForm.franchise_id ? Number(expenseForm.franchise_id) : null,
      };
      const res = await financeApi.post('/finance/expenses', payload);
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg('Expense logged successfully');
        fetchExpenses();
        fetchDashboard();
        fetchLedger();
        setExpenseForm({ ...expenseForm, amount: '', category: '', description: '' });
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Error', true);
    } finally { setLoading(false); }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await financeApi.post('/finance/employees', employeeForm);
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg('Employee created');
        fetchEmployees();
        setEmployeeForm({ name: '', email: '', password: '' });
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || 'Error', true);
    } finally { setLoading(false); }
  };

  const startEditEmployeePassword = (emp) => {
    setEditingEmployeeId(emp.id);
    setEmployeePasswordForm('');
  };

  const cancelEditEmployeePassword = () => {
    setEditingEmployeeId(null);
    setEmployeePasswordForm('');
  };

  const handleUpdateEmployeePassword = async (e) => {
    e.preventDefault();
    if (!editingEmployeeId) return;
    setLoading(true);
    try {
      const res = await financeApi.put(`/finance/employees/${editingEmployeeId}/password`, {
        password: employeePasswordForm,
      });
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg('Password updated');
        cancelEditEmployeePassword();
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Error', true);
    } finally { setLoading(false); }
  };

  const emptyFranchiseForm = () => ({
    name: '', city: '', total_amount_with_gst: '', cash_to_be_paid: '',
  });

  const buildFranchisePayload = () => ({
    name: franchiseForm.name,
    city: franchiseForm.city,
    total_amount_with_gst: franchiseForm.total_amount_with_gst !== '' ? Number(franchiseForm.total_amount_with_gst) : null,
    cash_to_be_paid: franchiseForm.cash_to_be_paid !== '' ? Number(franchiseForm.cash_to_be_paid) : null,
  });

  const handleCreateFranchise = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await financeApi.post('/finance/franchises', buildFranchisePayload());
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg('Franchise created');
        fetchFranchises();
        setFranchiseForm(emptyFranchiseForm());
        setEditingFranchiseId(null);
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || 'Error', true);
    } finally { setLoading(false); }
  };

  const handleUpdateFranchise = async (e) => {
    e.preventDefault();
    if (!editingFranchiseId) return;
    setLoading(true);
    try {
      const res = await financeApi.put(`/finance/franchises/${editingFranchiseId}`, buildFranchisePayload());
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg('Franchise updated');
        fetchFranchises();
        setFranchiseForm(emptyFranchiseForm());
        setEditingFranchiseId(null);
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || 'Error', true);
    } finally { setLoading(false); }
  };

  const startEditFranchise = (franchise) => {
    setEditingFranchiseId(franchise.id);
    setFranchiseForm({
      name: franchise.name,
      city: franchise.city || '',
      total_amount_with_gst: franchise.total_amount_with_gst ?? '',
      cash_to_be_paid: franchise.cash_to_be_paid ?? '',
    });
  };

  const cancelEditFranchise = () => {
    setEditingFranchiseId(null);
    setFranchiseForm(emptyFranchiseForm());
  };

  const tabLabel = (t) => t.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const visibleCollections = filterCollectionSearch(collections, collectionSearch);

  const collectionTotalAmount = (list, isCompany) => list.reduce(
    (sum, c) => sum + (isCompany ? (c.total_amount ?? c.franchise_amount + c.gst_amount) : (c.cash_amount || 0)),
    0,
  );

  const groupCollectionsByFranchise = (list) => {
    const groups = {};
    list.forEach((c) => {
      const id = c.franchise_id;
      if (!groups[id]) {
        groups[id] = { franchiseId: id, franchiseName: c.franchise_name || 'Unknown', records: [] };
      }
      groups[id].records.push(c);
    });
    return Object.values(groups)
      .map((g) => ({
        ...g,
        records: [...g.records].sort((a, b) => b.collection_date.localeCompare(a.collection_date)),
      }))
      .sort((a, b) => {
        const aDate = a.records[0]?.collection_date || '';
        const bDate = b.records[0]?.collection_date || '';
        return bDate.localeCompare(aDate);
      });
  };

  const toggleCollectionGroup = (franchiseId) => {
    setExpandedCollectionGroups((prev) => ({ ...prev, [franchiseId]: !prev[franchiseId] }));
  };

  const expandAllCollectionGroups = () => {
    const groups = groupCollectionsByFranchise(visibleCollections);
    const next = {};
    groups.forEach((g) => { next[g.franchiseId] = true; });
    setExpandedCollectionGroups(next);
  };

  const collapseAllCollectionGroups = () => setExpandedCollectionGroups({});

  const renderCompanyCollectionRows = (list, showFranchise = true) => list.map((c) => (
    <tr key={c.id}>
      <td>{c.collection_date}</td>
      {showFranchise && <td>{c.franchise_name}</td>}
      <td className="finance-amount credit">{formatINR(c.total_amount ?? c.franchise_amount + c.gst_amount)}</td>
      <td className="finance-amount">{formatINR(c.franchise_amount)}</td>
      <td>{formatINR(c.gst_amount)}</td>
      <td>{c.notes || '—'}</td>
    </tr>
  ));

  const renderCashCollectionRows = (list, showFranchise = true) => list.map((c) => (
    <tr key={c.id}>
      <td>{c.collection_date}</td>
      {showFranchise && <td>{c.franchise_name}</td>}
      <td className="finance-amount credit">{formatINR(c.cash_amount)}</td>
      <td>{c.assigned_employee_name || '—'}</td>
      <td>{c.notes || '—'}</td>
    </tr>
  ));

  const renderCollectionRecords = () => {
    const isCompany = adminFlow === 'company';
    const list = visibleCollections;
    const colSpan = isCompany ? 6 : 5;
    const showGrouped = !collectionFranchiseFilter && list.length > 0;

    if (list.length === 0) {
      return (
        <div className="finance-table-wrap">
          <table className="finance-table">
            <thead>
              {isCompany ? (
                <tr><th>Date</th><th>Franchise</th><th>Total</th><th>Base</th><th>GST</th><th>Notes</th></tr>
              ) : (
                <tr><th>Date</th><th>Franchise</th><th>Cash</th><th>Employee</th><th>Notes</th></tr>
              )}
            </thead>
            <tbody>
              <tr><td colSpan={colSpan} className="finance-empty">No records match your filters</td></tr>
            </tbody>
          </table>
        </div>
      );
    }

    if (showGrouped) {
      const groups = groupCollectionsByFranchise(list);
      return (
        <div className="finance-collection-groups">
          {groups.map((group) => {
            const expanded = !!expandedCollectionGroups[group.franchiseId];
            const groupTotal = collectionTotalAmount(group.records, isCompany);
            return (
              <div key={group.franchiseId} className="finance-collection-group">
                <button
                  type="button"
                  className="finance-collection-group-header"
                  onClick={() => toggleCollectionGroup(group.franchiseId)}
                  aria-expanded={expanded}
                >
                  <span className="finance-collection-group-toggle">
                    {expanded ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  <span className="finance-collection-group-name">{group.franchiseName}</span>
                  <span className="finance-collection-group-meta">
                    {group.records.length} {group.records.length === 1 ? 'record' : 'records'}
                  </span>
                  <span className="finance-collection-group-total">{formatINR(groupTotal)}</span>
                </button>
                {expanded && (
                  <div className="finance-table-wrap finance-collection-group-body">
                    <table className="finance-table">
                      <thead>
                        {isCompany ? (
                          <tr><th>Date</th><th>Total</th><th>Base</th><th>GST</th><th>Notes</th></tr>
                        ) : (
                          <tr><th>Date</th><th>Cash</th><th>Employee</th><th>Notes</th></tr>
                        )}
                      </thead>
                      <tbody>
                        {isCompany
                          ? renderCompanyCollectionRows(group.records, false)
                          : renderCashCollectionRows(group.records, false)}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="finance-table-wrap">
        <table className="finance-table">
          <thead>
            {isCompany ? (
              <tr><th>Date</th><th>Total</th><th>Base</th><th>GST</th><th>Notes</th></tr>
            ) : (
              <tr><th>Date</th><th>Cash</th><th>Employee</th><th>Notes</th></tr>
            )}
          </thead>
          <tbody>
            {isCompany
              ? renderCompanyCollectionRows(list, false)
              : renderCashCollectionRows(list, false)}
          </tbody>
        </table>
      </div>
    );
  };

  const handleCreateExpenseForFlow = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const source = adminFlow === 'company' ? 'COMPANY_ACCOUNT' : 'EMPLOYEE_CASH';
      const payload = {
        amount: Number(expenseForm.amount),
        source,
        category: expenseForm.category,
        description: expenseForm.description || undefined,
        expense_date: expenseForm.expense_date,
      };
      if (expenseForm.franchise_id) payload.franchise_id = Number(expenseForm.franchise_id);
      if (expenseForm.employee_id) payload.employee_id = Number(expenseForm.employee_id);
      if (adminFlow === 'company' && expenseForm.vendor.trim()) payload.vendor = expenseForm.vendor.trim();
      const res = await financeApi.post('/finance/expenses', payload);
      if (res.data?.status?.statusCode === 'code-200') {
        showMsg(
          adminFlow === 'company' && expenseForm.employee_id
            ? 'Payment recorded — amount added to employee cash balance'
            : 'Expense logged successfully',
        );
        fetchExpenses();
        fetchDashboard();
        fetchEmployees();
        setExpenseForm({ ...expenseForm, amount: '', category: '', description: '', employee_id: '', vendor: '', source });
      } else showMsg(res.data?.status?.statusMessage || 'Failed', true);
    } catch (err) {
      showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Error', true);
    } finally { setLoading(false); }
  };

  const handleDownloadCompanyBook = async () => {
    setBookDownloading(true);
    try {
      const params = { period: bookPeriod };
      if (bookPeriod === 'monthly') params.month_year = bookMonth;
      else if (bookPeriod === 'yearly') params.year = bookDownloadYear;
      else {
        params.start_month = bookStartMonth;
        params.end_month = bookEndMonth;
      }
      const res = await financeApi.get('/finance/reports/company-book/download', {
        params,
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || 'Company_Account_Book.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showMsg('Company account book downloaded');
    } catch (err) {
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const json = JSON.parse(text);
          showMsg(json?.status?.error || json?.status?.statusMessage || 'Download failed', true);
        } catch {
          showMsg('Download failed', true);
        }
      } else {
        showMsg(err.response?.data?.status?.error || err.response?.data?.status?.statusMessage || 'Download failed', true);
      }
    } finally {
      setBookDownloading(false);
    }
  };


  return (
    <div className="finance-page">
      <header className="finance-header">
        <div className="finance-header-brand">
          <div className="finance-header-logo">
            <img src={logo} alt="Cleanz24" className="finance-header-logo-img" />
          </div>
          <div>
            <h1>Finance Portal</h1>
            <p className="finance-header-subtitle">Your money story — simple, clear, and easy to follow</p>
          </div>
        </div>
        <div className="finance-header-actions">
          <span className="finance-user-badge"><FaUserCircle /> {user?.name} · {user?.role}</span>
          <button className="finance-logout-btn" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>

      {error && <div className="finance-error">{error}</div>}
      {success && <div className="finance-success">{success}</div>}

      <div className="finance-tabs">
        {tabs.map((t) => {
          const TabIcon = TAB_ICONS[t];
          return (
            <button key={t} className={`finance-tab ${activeTab === t ? 'active' : ''}`} onClick={() => handleTabChange(t)}>
              {TabIcon && <TabIcon className="finance-tab-icon" />}
              {tabLabel(t)}
            </button>
          );
        })}
      </div>

      <div className="finance-panel">
        {loading && !dashboard && (
          <div className="finance-loading">
            <div className="finance-loading-spinner" aria-hidden="true" />
            <p>Loading your dashboard…</p>
          </div>
        )}

        {activeTab === 'overview' && dashboard && (
          <>
            <div className="finance-welcome-banner">
              <div className="finance-welcome-copy">
                <span className="finance-welcome-eyebrow">{getGreeting()}</span>
                <h2 className="finance-welcome-title">{user?.name?.split(' ')[0] || 'there'}</h2>
                <p className="finance-welcome-desc">
                  {isAdmin
                    ? (adminFlow === 'company'
                      ? 'Follow the blue cards below — money flows from franchise stores into the company account, then out as expenses.'
                      : 'Follow the blue cards below — cash moves from stores to employees, then gets logged as expenses.')
                    : 'Your wallet in numbers — cash you hold and what you have spent so far.'}
                </p>
              </div>
              <img src={financeOverview} alt="" className="finance-welcome-art" aria-hidden="true" />
            </div>
            {isAdmin && <FlowSubTabs value={adminFlow} onChange={handleAdminFlowChange} />}
            {isAdmin && (
              <FlowGuide
                title="How money moves"
                steps={adminFlow === 'company' ? COMPANY_FLOW_STEPS : CASH_FLOW_STEPS}
              />
            )}
            {!isAdmin && (
              <TabStory {...getTabStory('overview', { isAdmin, adminFlow })} />
            )}
            <div className="finance-stats-grid finance-stats-grid--overview">
              {isAdmin ? (
                adminFlow === 'company' ? (
                  <>
                    <StatCard
                      label="Company Balance"
                      value={formatINR(dashboard.company?.balance ?? dashboard.company_balance)}
                      tone="primary"
                      icon={FaWallet}
                      chart="area"
                      info="Net company account position after franchise payments and expenses."
                      tag="Account"
                    />
                    <StatCard
                      label="Payments Received"
                      value={formatINR(dashboard.company?.payments_received ?? dashboard.total_company_payments)}
                      tone="success"
                      icon={FaArrowDown}
                      chart="bars"
                      info="Total GST-inclusive payments recorded from active franchises."
                      tag="Inflow"
                    />
                    <StatCard
                      label="Company Expenses"
                      value={formatINR(dashboard.company?.expenses)}
                      tone="danger"
                      icon={FaArrowUp}
                      chart="steps"
                      info={
                        (dashboard.company?.payments_received ?? dashboard.total_company_payments) > 0
                          ? `${pct(dashboard.company?.expenses, dashboard.company?.payments_received ?? dashboard.total_company_payments) ?? 0}% of payments spent this period.`
                          : 'Company account outflows logged against operations.'
                      }
                      tag="Outflow"
                    />
                    <StatCard
                      label="Active Franchises"
                      value={dashboard.active_franchises}
                      tone="neutral"
                      icon={FaStore}
                      chart="ring"
                      info="Franchise partners currently onboarded and active in the system."
                      tag="Network"
                    />
                  </>
                ) : (
                  <>
                    <StatCard
                      label="Cash from Franchises"
                      value={formatINR(dashboard.cash?.from_franchises ?? dashboard.total_cash_from_franchises)}
                      tone="warning"
                      icon={FaHandHoldingUsd}
                      chart="bars"
                      info="Cash collected from stores and assigned to field employees."
                      tag="Collections"
                    />
                    <StatCard
                      label="Total Cash Outstanding"
                      value={formatINR(dashboard.cash?.total_outstanding)}
                      tone="primary"
                      icon={FaCoins}
                      chart="area"
                      info={`Held across ${dashboard.active_employees ?? 0} active employees awaiting settlement.`}
                      tag="Outstanding"
                    />
                    <StatCard
                      label="Cash Expenses"
                      value={formatINR(dashboard.cash?.expenses)}
                      tone="danger"
                      icon={FaReceipt}
                      chart="line"
                      info={
                        (dashboard.cash?.from_franchises ?? dashboard.total_cash_from_franchises) > 0
                          ? `${pct(dashboard.cash?.expenses, dashboard.cash?.from_franchises ?? dashboard.total_cash_from_franchises) ?? 0}% of cash collections spent.`
                          : 'Employee cash expenses recorded this period.'
                      }
                      tag="Spends"
                    />
                    <StatCard
                      label="Active Employees"
                      value={dashboard.active_employees}
                      tone="neutral"
                      icon={FaUsers}
                      chart="ring"
                      info="Team members with active access to record collections and expenses."
                      tag="Team"
                    />
                  </>
                )
              ) : (
                <>
                  <StatCard
                    label="Your Cash Balance"
                    value={formatINR(dashboard.employee_cash_balance)}
                    tone="warning"
                    icon={FaWallet}
                    chart="area"
                    info="Cash currently assigned to you from franchise collections."
                    tag="Balance"
                  />
                  <StatCard
                    label="Your Expenses"
                    value={formatINR(dashboard.total_expenses)}
                    tone="danger"
                    icon={FaReceipt}
                    chart="steps"
                    info="Total expenses you have logged against your cash ledger."
                    tag="Spends"
                  />
                </>
              )}
            </div>
          </>
        )}

        {activeTab === 'ledger' && !isAdmin && (
          <>
            <TabStory {...getTabStory('ledger', { isAdmin, adminFlow })} />
            <div className="finance-stats-grid finance-stats-grid--ledger">
              <StatCard label="Current Balance" value={formatINR(ledgerReport?.cash_balance ?? dashboard?.employee_cash_balance)} tone="warning" icon={FaWallet} chart="area" info="Available cash in your ledger right now." tag="Balance" />
              <StatCard label="Credits This Month" value={formatINR(ledgerReport?.period_credits ?? 0)} tone="success" icon={FaPlusCircle} chart="bars" info="Cash credited to your account this month." tag="Credits" />
              <StatCard label="Debits This Month" value={formatINR(ledgerReport?.period_debits ?? 0)} tone="danger" icon={FaMinusCircle} chart="steps" info="Expenses and debits recorded this month." tag="Debits" />
            </div>
            <div className="finance-record-toolbar finance-expense-month-toolbar">
              <div className="finance-form-group">
                <label>Pick a month to read your story</label>
                <select
                  value={ledgerMonthFilter}
                  onChange={(e) => setLedgerMonthFilter(e.target.value)}
                >
                  {EXPENSE_MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <ListPanel icon={FaListAlt} title="Every credit & debit" count={ledgerReport?.entries?.length ?? 0}>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Details</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {!ledgerReport || ledgerReport.entries.length === 0 ? (
                    <tr><td colSpan="5" className="finance-empty">No ledger entries for this month</td></tr>
                  ) : ledgerReport.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.entry_date}</td>
                      <td>
                        <span className={`finance-ledger-badge ${entry.entry_type === 'CREDIT' ? 'credit' : 'debit'}`}>
                          {entry.entry_type === 'CREDIT' ? 'Credit' : 'Debit'}
                        </span>
                      </td>
                      <td>{entry.source_label}</td>
                      <td>{entry.detail || '—'}</td>
                      <td className={`finance-amount ${entry.entry_type === 'CREDIT' ? 'credit' : 'debit'}`}>
                        {entry.entry_type === 'CREDIT' ? '+' : '−'}{formatINR(entry.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </ListPanel>
          </>
        )}

        {activeTab === 'collections' && isAdmin && (
          <>
            <TabStory {...getTabStory('collections', { isAdmin, adminFlow })} />
            <FlowSubTabs value={adminFlow} onChange={handleAdminFlowChange} />
            <HelpTip>
              {adminFlow === 'company'
                ? 'Company payments are bank transfers with GST. Record each payment when a franchise pays — partial payments are fine.'
                : 'Cash collections assign money to an employee. Their ledger balance increases as soon as you save.'}
            </HelpTip>
            {adminFlow === 'company' ? (
              <FormPanel icon={FaHandHoldingUsd} title="New company payment" hint="Who paid, how much (with GST), and when.">
              <form className="finance-form" onSubmit={handleCompanyPayment}>
                <div className="finance-form-group">
                  <label>Franchise</label>
                  <select value={companyPaymentForm.franchise_id} onChange={(e) => setCompanyPaymentForm({ ...companyPaymentForm, franchise_id: e.target.value })} required>
                    <option value="">Select franchise</option>
                    {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Amount Paid (incl. {companyPaymentForm.gst_rate || 18}% GST)</label>
                  <input type="number" step="0.01" min="0.01" value={companyPaymentForm.total_amount} onChange={(e) => setCompanyPaymentForm({ ...companyPaymentForm, total_amount: e.target.value })} placeholder="e.g. 30000 for partial payment" required />
                </div>
                <div className="finance-form-group">
                  <label>GST Rate (%)</label>
                  <input type="number" step="0.01" min="0.01" value={companyPaymentForm.gst_rate} onChange={(e) => setCompanyPaymentForm({ ...companyPaymentForm, gst_rate: e.target.value })} required />
                </div>
                {companyPaymentBreakdown && (
                  <div className="finance-form-group" style={{ gridColumn: '1 / -1' }}>
                    <div className="finance-gst-breakdown">
                      <span>Base (excl. GST): <strong>{formatINR(companyPaymentBreakdown.base)}</strong></span>
                      <span>GST ({companyPaymentForm.gst_rate}%): <strong>{formatINR(companyPaymentBreakdown.gst)}</strong></span>
                      <span>Total: <strong>{formatINR(companyPaymentBreakdown.total)}</strong></span>
                    </div>
                  </div>
                )}
                <div className="finance-form-group">
                  <label>Payment Date</label>
                  <input type="date" value={companyPaymentForm.collection_date} onChange={(e) => setCompanyPaymentForm({ ...companyPaymentForm, collection_date: e.target.value })} required />
                </div>
                <div className="finance-form-group">
                  <label>Notes</label>
                  <input type="text" value={companyPaymentForm.notes} onChange={(e) => setCompanyPaymentForm({ ...companyPaymentForm, notes: e.target.value })} placeholder="e.g. Part 1 of 3" />
                </div>
                <div className="finance-form-actions-row">
                  <button type="submit" className="finance-btn" disabled={loading}>
                    <FaPlus /> Record Company Payment
                  </button>
                </div>
              </form>
              </FormPanel>
            ) : (
              <FormPanel icon={FaMoneyBillWave} title="New cash collection" hint="Which store gave cash, to which employee, and how much.">
              <form className="finance-form" onSubmit={handleCashCollection}>
                <div className="finance-form-group">
                  <label>Franchise</label>
                  <select value={cashCollectionForm.franchise_id} onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, franchise_id: e.target.value })} required>
                    <option value="">Select franchise</option>
                    {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Cash Amount</label>
                  <input type="number" step="0.01" min="0.01" value={cashCollectionForm.cash_amount} onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, cash_amount: e.target.value })} required />
                </div>
                <div className="finance-form-group">
                  <label>Employee</label>
                  <select value={cashCollectionForm.assigned_employee_id} onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, assigned_employee_id: e.target.value })} required>
                    <option value="">Select employee</option>
                    {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                  </select>
                </div>
                <div className="finance-form-group">
                  <label>Payment Date</label>
                  <input type="date" value={cashCollectionForm.collection_date} onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, collection_date: e.target.value })} required />
                </div>
                <div className="finance-form-group">
                  <label>Notes</label>
                  <input type="text" value={cashCollectionForm.notes} onChange={(e) => setCashCollectionForm({ ...cashCollectionForm, notes: e.target.value })} />
                </div>
                <div className="finance-form-actions-row">
                  <button type="submit" className="finance-btn" disabled={loading}>
                    <FaPlus /> Record Cash Collection
                  </button>
                </div>
              </form>
              </FormPanel>
            )}
            <ListPanel icon={FaListAlt} title="Past collections" count={visibleCollections.length}>
            <div className="finance-record-toolbar">
              <div className="finance-form-group">
                <label>Filter by franchise</label>
                <select
                  value={collectionFranchiseFilter}
                  onChange={(e) => {
                    setCollectionFranchiseFilter(e.target.value);
                    setExpandedCollectionGroups({});
                  }}
                >
                  <option value="">All franchises</option>
                  {franchises.map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div className="finance-form-group finance-record-search">
                <label>Search</label>
                <div className="finance-search-input">
                  <FaSearch aria-hidden />
                  <input
                    type="text"
                    value={collectionSearch}
                    onChange={(e) => setCollectionSearch(e.target.value)}
                    placeholder="Notes, employee…"
                  />
                </div>
              </div>
              {!collectionFranchiseFilter && visibleCollections.length > 0 && (
                <div className="finance-record-toolbar-actions">
                  <button type="button" className="finance-btn secondary" onClick={expandAllCollectionGroups}>
                    Expand all
                  </button>
                  <button type="button" className="finance-btn secondary" onClick={collapseAllCollectionGroups}>
                    Collapse all
                  </button>
                </div>
              )}
            </div>
            {renderCollectionRecords()}
            </ListPanel>
          </>
        )}

        {activeTab === 'expenses' && (
          <>
            <TabStory {...getTabStory('expenses', { isAdmin, adminFlow })} />
            {isAdmin && <FlowSubTabs value={adminFlow} onChange={handleAdminFlowChange} />}
            {isAdmin && (
              <HelpTip>
                {adminFlow === 'company'
                  ? 'Pay a vendor OR pay an employee from the company account — pick one. Employee payments add to their cash balance.'
                  : 'This deducts from the selected employee\'s cash balance. Make sure they have enough held cash.'}
              </HelpTip>
            )}
            <FormPanel
              icon={FaReceipt}
              title={isAdmin ? (adminFlow === 'company' ? 'New company expense' : 'New employee cash expense') : 'Log your expense'}
              hint={isAdmin ? 'Amount, category, and date — the essentials.' : 'Spends come from your cash balance automatically.'}
            >
            <form className="finance-form" onSubmit={isAdmin ? handleCreateExpenseForFlow : handleCreateExpense}>
              <div className="finance-form-group">
                <label>Amount</label>
                <input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required />
              </div>
              {!isAdmin && (
                <div className="finance-form-group">
                  <label>Source</label>
                  <input type="text" readOnly value="Employee Cash" className="finance-readonly" />
                </div>
              )}
              {isAdmin && (
                <>
                  <div className="finance-form-group">
                    <label>Source</label>
                    <input type="text" readOnly value={adminFlow === 'company' ? 'Company Account' : 'Employee Cash'} className="finance-readonly" />
                  </div>
                  {adminFlow === 'company' ? (
                    <>
                      <div className="finance-form-group">
                        <label>Pay to employee (optional)</label>
                        <select
                          value={expenseForm.employee_id}
                          onChange={(e) => setExpenseForm({ ...expenseForm, employee_id: e.target.value, vendor: '' })}
                          disabled={Boolean(expenseForm.vendor.trim())}
                        >
                          <option value="">None — paying a vendor</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>{emp.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="finance-form-group">
                        <label>Vendor (optional)</label>
                        <input
                          type="text"
                          value={expenseForm.vendor}
                          onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value, employee_id: '' })}
                          placeholder="Vendor / supplier name"
                          disabled={Boolean(expenseForm.employee_id)}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="finance-form-group">
                      <label>Employee</label>
                      <select
                        value={expenseForm.employee_id}
                        onChange={(e) => setExpenseForm({ ...expenseForm, employee_id: e.target.value })}
                        required
                      >
                        <option value="">Select employee</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name} (Balance: {formatINR(emp.cash_balance)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
              <div className="finance-form-group">
                <label>Category</label>
                <input type="text" value={expenseForm.category} onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })} required />
              </div>
              <div className="finance-form-group">
                <label>Franchise (optional)</label>
                <select value={expenseForm.franchise_id} onChange={(e) => setExpenseForm({ ...expenseForm, franchise_id: e.target.value })}>
                  <option value="">None</option>
                  {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="finance-form-group">
                <label>Expense Date</label>
                <input type="date" value={expenseForm.expense_date} onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })} required />
              </div>
              <div className="finance-form-group">
                <label>Description</label>
                <input type="text" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} />
              </div>
              <div className="finance-form-actions-row">
                <button type="submit" className="finance-btn" disabled={loading}><FaPlus /> Log Expense</button>
              </div>
            </form>
            </FormPanel>
            <ListPanel icon={FaListAlt} title="Expense history" count={expenses.length}>
            <div className="finance-record-toolbar finance-expense-month-toolbar">
              <div className="finance-form-group">
                <label>Pick a month</label>
                <select
                  value={expenseMonthFilter}
                  onChange={(e) => setExpenseMonthFilter(e.target.value)}
                >
                  {EXPENSE_MONTH_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    {isAdmin && <th>Payee</th>}
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Franchise</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 5 : 4} className="finance-empty">
                        No {isAdmin ? (adminFlow === 'company' ? 'company account' : 'employee cash') : ''} expenses for{' '}
                        {EXPENSE_MONTH_OPTIONS.find((o) => o.value === expenseMonthFilter)?.label || 'this month'}.
                        {isAdmin && adminFlow === 'cash' ? ' Switch to Company tab for company account expenses.' : ''}
                        {isAdmin && adminFlow === 'company' ? ' Switch to Cash tab for employee cash expenses.' : ''}
                      </td>
                    </tr>
                  ) : expenses.map((ex) => (
                    <tr key={ex.id}>
                      <td>{ex.expense_date}</td>
                      {isAdmin && <td>{ex.payee_name || '—'}</td>}
                      <td>{ex.category}</td>
                      <td className="finance-amount debit">{formatINR(ex.amount)}</td>
                      <td>{ex.franchise_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </ListPanel>
          </>
        )}

        {activeTab === 'employees' && isAdmin && (
          <>
            <TabStory {...getTabStory('employees', { isAdmin, adminFlow })} />
            <HelpTip>
              Cash balance is calculated from ledger entries. When a franchise gives cash to someone, use <strong>Collections → Cash in Hand</strong>.
            </HelpTip>
            <FormPanel icon={FaUserFriends} title="Add a new employee" hint="Name, email, and a login password — they can then log expenses.">
            <form className="finance-form" onSubmit={handleCreateEmployee}>
              <div className="finance-form-group">
                <label>Name</label>
                <input type="text" value={employeeForm.name} onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })} required />
              </div>
              <div className="finance-form-group">
                <label>Email</label>
                <input type="email" value={employeeForm.email} onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })} required />
              </div>
              <div className="finance-form-group">
                <label>Password</label>
                <PasswordInput
                  value={employeeForm.password}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
              <div className="finance-form-actions-row">
                <button type="submit" className="finance-btn" disabled={loading}><FaPlus /> Add Employee</button>
              </div>
            </form>
            </FormPanel>
            <ListPanel icon={FaUsers} title="Team roster" count={employees.length}>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead><tr><th>Name</th><th>Email</th><th>Cash Balance</th><th></th></tr></thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr><td colSpan="4" className="finance-empty">No employees yet</td></tr>
                  ) : employees.map((emp) => (
                    <React.Fragment key={emp.id}>
                      <tr className={editingEmployeeId === emp.id ? 'finance-row-editing' : ''}>
                        <td>{emp.name}</td>
                        <td>{emp.email}</td>
                        <td className="finance-amount">{formatINR(emp.cash_balance)}</td>
                        <td>
                          <button
                            type="button"
                            className="finance-edit-btn"
                            onClick={() => startEditEmployeePassword(emp)}
                            title="Reset password"
                          >
                            <FaEdit /> Reset Password
                          </button>
                        </td>
                      </tr>
                      {editingEmployeeId === emp.id && (
                        <tr className="finance-row-editing">
                          <td colSpan="4">
                            <form className="finance-inline-form" onSubmit={handleUpdateEmployeePassword}>
                              <span className="finance-inline-form-label">New password for <strong>{emp.name}</strong></span>
                              <PasswordInput
                                value={employeePasswordForm}
                                onChange={(e) => setEmployeePasswordForm(e.target.value)}
                                placeholder="Enter new password"
                                required
                                autoFocus
                                disabled={loading}
                              />
                              <div className="finance-form-actions">
                                <button type="submit" className="finance-btn" disabled={loading}>Save Password</button>
                                <button type="button" className="finance-btn secondary" onClick={cancelEditEmployeePassword} disabled={loading}>
                                  <FaTimes /> Cancel
                                </button>
                              </div>
                            </form>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            </ListPanel>
          </>
        )}

        {activeTab === 'franchises' && isAdmin && (
          <>
            <TabStory {...getTabStory('franchises', { isAdmin, adminFlow })} />
            <HelpTip>
              Total with GST and cash due are reference numbers for planning — they do not change ledger balances.
            </HelpTip>
            <FormPanel
              icon={FaStore}
              title={editingFranchiseId ? 'Edit franchise' : 'Add a franchise'}
              hint="Store name and city. Optional reference amounts help you track what each store owes."
            >
            <form className="finance-form" onSubmit={editingFranchiseId ? handleUpdateFranchise : handleCreateFranchise}>
              <div className="finance-form-group">
                <label>Name</label>
                <input type="text" value={franchiseForm.name} onChange={(e) => setFranchiseForm({ ...franchiseForm, name: e.target.value })} required />
              </div>
              <div className="finance-form-group">
                <label>City</label>
                <input type="text" value={franchiseForm.city} onChange={(e) => setFranchiseForm({ ...franchiseForm, city: e.target.value })} />
              </div>
              <div className="finance-form-group">
                <label>Total Amount with GST (info)</label>
                <input type="number" step="0.01" min="0" value={franchiseForm.total_amount_with_gst} onChange={(e) => setFranchiseForm({ ...franchiseForm, total_amount_with_gst: e.target.value })} placeholder="Full amount franchise owes" />
              </div>
              <div className="finance-form-group">
                <label>Cash to be Paid (info)</label>
                <input type="number" step="0.01" min="0" value={franchiseForm.cash_to_be_paid} onChange={(e) => setFranchiseForm({ ...franchiseForm, cash_to_be_paid: e.target.value })} placeholder="Total cash franchise will pay" />
              </div>
              <div className="finance-form-actions-row finance-form-actions">
                {editingFranchiseId ? (
                  <>
                    <button type="submit" className="finance-btn" disabled={loading}>Save Changes</button>
                    <button type="button" className="finance-btn secondary" onClick={cancelEditFranchise} disabled={loading}>
                      <FaTimes /> Cancel
                    </button>
                  </>
                ) : (
                  <button type="submit" className="finance-btn" disabled={loading}><FaPlus /> Add Franchise</button>
                )}
              </div>
            </form>
            </FormPanel>
            <ListPanel icon={FaStore} title="All franchises" count={franchises.length}>
            <div className="finance-table-wrap">
              <table className="finance-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>City</th>
                    <th>Total with GST</th>
                    <th>Cash to be Paid</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {franchises.length === 0 ? (
                    <tr><td colSpan="5" className="finance-empty">No franchises yet</td></tr>
                  ) : franchises.map((f) => (
                    <tr key={f.id} className={editingFranchiseId === f.id ? 'finance-row-editing' : ''}>
                      <td>{f.name}</td>
                      <td>{f.city || '—'}</td>
                      <td className="finance-amount">{f.total_amount_with_gst != null ? formatINR(f.total_amount_with_gst) : '—'}</td>
                      <td className="finance-amount">{f.cash_to_be_paid != null ? formatINR(f.cash_to_be_paid) : '—'}</td>
                      <td>
                        <button type="button" className="finance-edit-btn" onClick={() => startEditFranchise(f)} title="Edit franchise">
                          <FaEdit /> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </ListPanel>
          </>
        )}

        {activeTab === 'reports' && isAdmin && (
          <>
            <TabStory {...getTabStory('reports', { isAdmin, adminFlow })} />
            <FlowSubTabs value={adminFlow} onChange={handleAdminFlowChange} />
            <div className="finance-record-toolbar finance-expense-month-toolbar">
              <div className="finance-form-group">
                <label>Report year</label>
                <input
                  type="number"
                  value={reportYear}
                  onChange={(e) => setReportYear(Number(e.target.value))}
                />
              </div>
            </div>
            {adminFlow === 'company' ? (
              <>
                <div className="finance-download-panel">
                  <div className="finance-download-panel-header">
                    <div>
                      <h3 style={{ margin: 0 }}>Download Company Account Book</h3>
                      <p className="finance-download-panel-desc">
                        Export a professional XLSX ledger with credits, debits, running balance, and franchise insights.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="finance-btn"
                      onClick={handleDownloadCompanyBook}
                      disabled={bookDownloading}
                    >
                      <FaDownload /> {bookDownloading ? 'Preparing…' : 'Download XLSX'}
                    </button>
                  </div>
                  <div className="finance-record-toolbar finance-download-toolbar">
                    <div className="finance-form-group">
                      <label>Period Type</label>
                      <select value={bookPeriod} onChange={(e) => setBookPeriod(e.target.value)}>
                        <option value="monthly">Monthly</option>
                        <option value="range">Month Range</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                    {bookPeriod === 'monthly' && (
                      <div className="finance-form-group">
                        <label>Month</label>
                        <select value={bookMonth} onChange={(e) => setBookMonth(e.target.value)}>
                          {EXPENSE_MONTH_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {bookPeriod === 'range' && (
                      <>
                        <div className="finance-form-group">
                          <label>From Month</label>
                          <select value={bookStartMonth} onChange={(e) => setBookStartMonth(e.target.value)}>
                            {EXPENSE_MONTH_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="finance-form-group">
                          <label>To Month</label>
                          <select value={bookEndMonth} onChange={(e) => setBookEndMonth(e.target.value)}>
                            {EXPENSE_MONTH_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                    {bookPeriod === 'yearly' && (
                      <div className="finance-form-group">
                        <label>Year</label>
                        <input
                          type="number"
                          value={bookDownloadYear}
                          onChange={(e) => setBookDownloadYear(Number(e.target.value))}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <ListPanel icon={FaStore} title="Franchise-wise company totals" count={franchiseReport.length}>
                <div className="finance-table-wrap" style={{ marginBottom: 0 }}>
                  <table className="finance-table">
                    <thead>
                      <tr><th>Franchise</th><th>Payments</th><th>Base</th><th>GST</th><th>Total</th><th>Expenses</th><th>Balance</th></tr>
                    </thead>
                    <tbody>
                      {franchiseReport.map((r) => (
                        <tr key={r.franchise_id}>
                          <td>{r.franchise_name}</td>
                          <td>{r.company_collection_count ?? r.collection_count}</td>
                          <td>{formatINR(r.total_franchise_amount)}</td>
                          <td>{formatINR(r.total_gst_amount)}</td>
                          <td className="finance-amount credit">{formatINR(r.total_company_payments ?? r.total_franchise_amount + r.total_gst_amount)}</td>
                          <td className="finance-amount debit">{formatINR(r.company_expenses ?? r.total_expenses)}</td>
                          <td className="finance-amount credit">{formatINR(r.company_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </ListPanel>
                <ListPanel icon={FaChartBar} title={`Monthly company report (${reportYear})`} count={monthlyReport.length}>
                <div className="finance-table-wrap">
                  <table className="finance-table">
                    <thead><tr><th>Month</th><th>Payments</th><th>Expenses</th><th>Net Inflow</th></tr></thead>
                    <tbody>
                      {monthlyReport.map((r) => (
                        <tr key={r.month}>
                          <td>{new Date(r.year, r.month - 1).toLocaleString('en', { month: 'long' })}</td>
                          <td>{formatINR(r.total_collections)}</td>
                          <td className="finance-amount debit">{formatINR(r.company_expenses ?? r.total_expenses)}</td>
                          <td className={`finance-amount ${(r.net_company_inflow ?? r.net_inflow) >= 0 ? 'credit' : 'debit'}`}>{formatINR(r.net_company_inflow ?? r.net_inflow)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </ListPanel>
              </>
            ) : (
              <>
                <ListPanel icon={FaStore} title="Franchise cash totals" count={franchiseReport.length}>
                <div className="finance-table-wrap" style={{ marginBottom: 0 }}>
                  <table className="finance-table">
                    <thead>
                      <tr><th>Franchise</th><th>Cash Collections</th><th>Cash Collected</th><th>Cash Expenses</th></tr>
                    </thead>
                    <tbody>
                      {franchiseReport.map((r) => (
                        <tr key={r.franchise_id}>
                          <td>{r.franchise_name}</td>
                          <td>{r.cash_collection_count ?? 0}</td>
                          <td className="finance-amount credit">{formatINR(r.total_cash_amount)}</td>
                          <td className="finance-amount debit">{formatINR(r.cash_expenses)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </ListPanel>
                <ListPanel icon={FaUsers} title="Employee cash outstanding" count={employeeCashReport.length}>
                <div className="finance-table-wrap" style={{ marginBottom: 0 }}>
                  <table className="finance-table">
                    <thead><tr><th>Employee</th><th>Credits</th><th>Debits</th><th>Outstanding</th></tr></thead>
                    <tbody>
                      {employeeCashReport.map((r) => (
                        <tr key={r.employee_id}>
                          <td>{r.employee_name}</td>
                          <td className="finance-amount credit">{formatINR(r.total_credits)}</td>
                          <td className="finance-amount debit">{formatINR(r.total_debits)}</td>
                          <td className="finance-amount">{formatINR(r.outstanding_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </ListPanel>
                <ListPanel icon={FaChartBar} title={`Monthly cash report (${reportYear})`} count={monthlyReport.length}>
                <div className="finance-table-wrap">
                  <table className="finance-table">
                    <thead><tr><th>Month</th><th>Cash from Franchises</th><th>Cash Expenses</th><th>Net Inflow</th></tr></thead>
                    <tbody>
                      {monthlyReport.map((r) => (
                        <tr key={r.month}>
                          <td>{new Date(r.year, r.month - 1).toLocaleString('en', { month: 'long' })}</td>
                          <td>{formatINR(r.total_cash_assigned)}</td>
                          <td className="finance-amount debit">{formatINR(r.cash_expenses)}</td>
                          <td className={`finance-amount ${(r.net_cash_inflow ?? 0) >= 0 ? 'credit' : 'debit'}`}>{formatINR(r.net_cash_inflow)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </ListPanel>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
